import { computed, makeObservable } from 'mobx';
import { Amount } from 'src_common/common/amount/Amount';
import { getDepositLimits, getRollingLimits } from 'src_server/trpc/types/responsibleGambling';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { UsersState } from 'src/domains/players/state/UsersState';
import { Common } from 'src/domains/common/Common';
import { AmountPrecision } from 'src_common/common/amount/AmountPrecision';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import {
    findMostRestrictiveDepositLimitUsage,
    getDepositLimitWindowLengths,
} from 'src/domains/players/shared/responsibleGambling/depositLimitUsage';

const calculateRemainingLimits = (
    depositLimits: getDepositLimits.SuccessResponseType['limits'],
    amountPrecision: AmountPrecision
): Array<Amount | undefined> => {
    return getDepositLimitWindowLengths(depositLimits)
        .map((limitDayNumber) => {
            const mostRestrictiveUsage = findMostRestrictiveDepositLimitUsage(depositLimits, limitDayNumber);
            if (mostRestrictiveUsage === undefined) {
                return undefined;
            }

            const rollingLimit = amountPrecision.newFromOld(mostRestrictiveUsage.amount);
            const usedLimit = amountPrecision.newFromOld(mostRestrictiveUsage.used);

            return rollingLimit.sub(usedLimit);
        })
        .filter((limit) => limit !== undefined);
};

export const getBindingLimitCalculationType = (
    depositLimits: getDepositLimits.SuccessResponseType['limits'],
    amountPrecision: AmountPrecision
): getRollingLimits.CalculationTypeSchemaType => {
    let bindingUsage: getDepositLimits.SuccessResponseType['limits'][number] | undefined;
    for (const limitDayNumber of getDepositLimitWindowLengths(depositLimits)) {
        const mostRestrictiveUsage = findMostRestrictiveDepositLimitUsage(depositLimits, limitDayNumber);
        if (mostRestrictiveUsage === undefined) {
            continue;
        }

        const rollingLimit = amountPrecision.newFromOld(mostRestrictiveUsage.amount);
        const usedLimit = amountPrecision.newFromOld(mostRestrictiveUsage.used);
        const remaining = rollingLimit.sub(usedLimit);

        if (bindingUsage === undefined) {
            bindingUsage = mostRestrictiveUsage;
            continue;
        }

        const currentRemaining = amountPrecision
            .newFromOld(bindingUsage.amount)
            .sub(amountPrecision.newFromOld(bindingUsage.used));

        if (remaining.isLessThan(currentRemaining)) {
            bindingUsage = mostRestrictiveUsage;
        }
    }

    if (bindingUsage === undefined) {
        return 'net';
    }

    return bindingUsage.calculationType;
};

const getLowestAmountValue = (amounts: Array<Amount | undefined>): Amount | undefined => {
    if (amounts.length === 0) {
        return undefined;
    }

    const amount = amounts.reduce((prev, curr) => {
        if (prev === undefined || curr === undefined) {
            return undefined;
        }

        return prev.isLessThan(curr) ? prev : curr;
    });

    return amount?.isLessThan(new Amount('0')) ? new Amount('0') : amount;
};

export class RemainingLimitState {
    public readonly depositLimitsDataResource: Resource<getDepositLimits.ResponseType>;

    public static get = AutoWeakMap.create((common: Common) => new RemainingLimitState(common, UsersState.get(common)));

    private constructor(
        private readonly common: Common,
        private readonly usersState: UsersState
    ) {
        makeObservable(this);

        this.usersState.rollingNetDepositLimitData.refresh();
        this.depositLimitsDataResource = new Resource(async (): Promise<getDepositLimits.ResponseType> => {
            const response = await this.common.trpcClient.client.responsibleGamblingRouter.getDepositLimits.query();
            return response;
        });
    }

    @computed public get remainingLimit(): Amount | undefined {
        const depositLimits = this.getDepositLimitsData;
        if (depositLimits === null) {
            return undefined;
        }

        const remainingLimits = calculateRemainingLimits(depositLimits, ConfigComponents.get(this.common).precision);
        return getLowestAmountValue(remainingLimits);
    }

    @computed public get bindingLimitCalculationType(): getRollingLimits.CalculationTypeSchemaType {
        const depositLimits = this.getDepositLimitsData;

        if (depositLimits === null) {
            return 'net';
        }

        return getBindingLimitCalculationType(depositLimits, ConfigComponents.get(this.common).precision);
    }

    @computed private get getDepositLimitsData(): getDepositLimits.SuccessResponseType['limits'] | null {
        const depositLimit = this.depositLimitsDataResource.getReady();

        if (depositLimit === null) {
            return null;
        }

        if (depositLimit.responseStatus === 'apiError') {
            return null;
        }

        return depositLimit.data.limits;
    }
}
